/**
 * Reservation Service
 * Functions to interact with reservation Cloud Functions
 */

import { httpsCallable } from 'firebase/functions'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore'
import { functions, db } from '@/config/firebase'
import { Reservation } from '@/types'

/**
 * Reserve an item
 */
export async function reserveItem(wishlistId: string, itemId: string, note?: string) {
  const reserveItemFn = httpsCallable(functions, 'reserveItem')
  const result = await reserveItemFn({ wishlistId, itemId, note })
  return result.data as { message: string }
}

/**
 * Cancel a reservation
 */
export async function unreserveItem(wishlistId: string, itemId: string) {
  const unreserveItemFn = httpsCallable(functions, 'unreserveItem')
  const result = await unreserveItemFn({ wishlistId, itemId })
  return result.data as { message: string }
}

/**
 * Get user's reservations
 */
export async function getMyReservations(): Promise<Reservation[]> {
  const getMyReservationsFn = httpsCallable(functions, 'getMyReservations')
  const result = await getMyReservationsFn()
  const data = result.data as { reservations: Reservation[] }
  return data.reservations
}

/**
 * Get user's reservations from Firestore (alternative to Cloud Function)
 */
export async function getUserReservations(userId: string): Promise<Reservation[]> {
  const q = query(
    collection(db, 'reservations'),
    where('userId', '==', userId),
    where('isActive', '==', true),
    orderBy('reservedAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Reservation[]
}

/**
 * Subscribe to user's reservations
 */
export function subscribeToUserReservations(
  userId: string,
  callback: (reservations: Reservation[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, 'reservations'),
    where('userId', '==', userId),
    where('isActive', '==', true),
    orderBy('reservedAt', 'desc')
  )

  return onSnapshot(
    q,
    snapshot => {
      const reservations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Reservation[]
      callback(reservations)
    },
    error => {
      console.error('Error subscribing to reservations:', error)
      if (onError) {
        onError(error)
      } else {
        // If no error handler provided, still call callback with empty array
        // to prevent infinite loading
        callback([])
      }
    }
  )
}
